import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Play, Zap, Target, Trophy, Beaker, FlaskConical } from 'lucide-react';
import type { ModeMetadata } from '../modes';
import { DjayAvatar, useDjaySelection, getDjayModeIntro } from '../mascot';
interface LabStationProps {
  mode: ModeMetadata;
  onClick: () => void;
}
const iconMap = {
  Zap,
  Target,
  Trophy,
  Beaker,
  FlaskConical
};
const colorMap: Record<string, {
  bg: string;
  text: string;
  border: string;
  gradient: string;
  shadow: string;
}> = {
  Zap: {
    bg: 'bg-amber-100',
    text: 'text-amber-600',
    border: 'border-amber-200',
    gradient: 'from-amber-400 to-orange-500',
    shadow: 'shadow-amber-200'
  },
  Target: {
    bg: 'bg-indigo-100',
    text: 'text-indigo-600',
    border: 'border-indigo-200',
    gradient: 'from-indigo-400 to-purple-500',
    shadow: 'shadow-indigo-200'
  },
  Trophy: {
    bg: 'bg-violet-100',
    text: 'text-violet-600',
    border: 'border-violet-200',
    gradient: 'from-violet-400 to-fuchsia-500',
    shadow: 'shadow-violet-200'
  },
  Beaker: {
    bg: 'bg-teal-100',
    text: 'text-teal-600',
    border: 'border-teal-200',
    gradient: 'from-teal-400 to-emerald-500',
    shadow: 'shadow-teal-200'
  },
  FlaskConical: {
    bg: 'bg-rose-100',
    text: 'text-rose-600',
    border: 'border-rose-200',
    gradient: 'from-rose-400 to-pink-500',
    shadow: 'shadow-rose-200'
  }
};
/**
 * LabStation — Interactive card for a single practice mode.
 *
 * Shows mode icon, title, description, tags, and a Djay hover tooltip.
 * Part of the learningLab domain (DDD co-locatie s57).
 */
export function LabStation({
  mode,
  onClick
}: LabStationProps) {
  const Icon = iconMap[mode.icon as keyof typeof iconMap] || Beaker;
  const colors = colorMap[mode.icon] || colorMap.Beaker;
  const isDisabled = !mode.isEnabled;
  const [isHovered, setIsHovered] = useState(false);
  const {
    selectedVariant
  } = useDjaySelection();
  const modeIntro = useMemo(() => getDjayModeIntro(mode.id), [mode.id]);
  return <motion.div whileHover={!isDisabled ? {
    y: -6,
    scale: 1.02
  } : {}} whileTap={!isDisabled ? {
    scale: 0.96
  } : {}} transition={{
    type: 'spring',
    stiffness: 400,
    damping: 25
  }} className="h-full relative" onMouseEnter={() => !isDisabled && setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} data-id="element-1783">
      {/* Djay hover tooltip */}
      <AnimatePresence data-id="element-1784">
        {isHovered && !isDisabled && <motion.div initial={{
        opacity: 0,
        y: 6,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 6,
        scale: 0.95
      }} transition={{
        type: 'spring',
        stiffness: 400,
        damping: 25
      }} className="absolute bottom-full left-0 right-0 mb-2 z-20 flex items-end gap-2 px-3 pointer-events-none" data-id="element-1785">
            <div className="flex-shrink-0" data-id="element-1786">
              <DjayAvatar size="sm" emotion={modeIntro.emotion} variant={selectedVariant} animate={false} data-id="element-1787" />
            </div>
            <div className="bg-white rounded-2xl shadow-lg border-2 border-slate-100 px-3 py-2 text-xs font-bold text-slate-700 flex-1 mb-1" data-id="element-1788">
              {modeIntro.text}
            </div>
          </motion.div>}
      </AnimatePresence>

      <div className={`h-full flex rounded-[2rem] border-4 transition-all relative overflow-hidden bg-white group
          ${isDisabled ? 'opacity-60 cursor-not-allowed border-slate-200' : 'cursor-pointer hover:border-transparent hover:shadow-xl border-slate-100'}`} onClick={!isDisabled ? onClick : undefined} data-id="element-1789">
        {/* Left Accent Bar */}
        <div className={`w-4 flex-shrink-0 ${isDisabled ? 'bg-slate-200' : `bg-gradient-to-b ${colors.gradient}`}`} data-id="element-1790" />

        <div className="p-6 flex flex-col h-full relative z-10 flex-1" data-id="element-1791">
          {/* Top Row: Icon & Time */}
          <div className="flex items-start justify-between mb-5" data-id="element-1792">
            <motion.div whileHover={!isDisabled ? {
            rotate: [0, -10, 10, -10, 0]
          } : {}} transition={{
            duration: 0.5
          }} className={`w-16 h-16 rounded-3xl ${isDisabled ? 'bg-slate-100' : colors.bg} flex items-center justify-center shadow-inner`} data-id="element-1793">
              <Icon className={`w-8 h-8 ${isDisabled ? 'text-slate-400' : colors.text}`} strokeWidth={2.5} data-id="element-1794" />
            </motion.div>

            <div className={`flex items-center gap-1.5 text-xs font-black px-3 py-1.5 rounded-full border-2 
              ${isDisabled ? 'bg-slate-50 text-slate-400 border-slate-100' : 'bg-slate-50 text-slate-600 border-slate-200'}`} data-id="element-1795">
              <Clock className="w-4 h-4" data-id="element-1796" />
              <span data-id="element-1797">{mode.estimatedMinutes}m</span>
            </div>
          </div>

          {/* Title & Subtitle */}
          <div className="mb-4 flex-1" data-id="element-1798">
            <h3 className={`text-2xl font-black tracking-tight leading-tight mb-1 ${isDisabled ? 'text-slate-500' : 'text-slate-800'}`} data-id="element-1799">
              {mode.title}
            </h3>
            <p className={`text-sm font-bold ${isDisabled ? 'text-slate-400' : colors.text}`} data-id="element-1800">
              {mode.subtitle}
            </p>
            <p className="text-sm text-slate-500 font-medium mt-2 line-clamp-2" data-id="element-1801">
              {mode.description}
            </p>
          </div>

          {/* Action Area */}
          <div className="mt-auto pt-4 flex items-center justify-between border-t-2 border-slate-50" data-id="element-1802">
            <div className="flex flex-wrap gap-1.5" data-id="element-1803">
              {mode.tags.slice(0, 2).map(tag => <span key={tag} className={`text-xs font-bold px-2.5 py-1 rounded-lg
                    ${isDisabled ? 'bg-slate-100 text-slate-400' : `${colors.bg} ${colors.text}`}`} data-id="element-1804">
                  {tag}
                </span>)}
            </div>

            {!isDisabled ? <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${colors.gradient} flex items-center justify-center shadow-lg text-white group-hover:scale-110 transition-transform flex-shrink-0`} data-id="element-1805">
                <Play className="w-6 h-6 ml-1" fill="currentColor" data-id="element-1806" />
              </div> : <span className="text-xs font-black text-slate-400 bg-slate-100 px-4 py-2 rounded-xl" data-id="element-1807">
                Binnenkort
              </span>}
          </div>
        </div>
      </div>
    </motion.div>;
}